import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import './Clients.css';

interface Client {
  id: number;
  name: string;
  phone: string;
  email?: string;
  projectsCount?: number;
  projects?: Array<{ id: number; name: string; status: string }>;
}

const Clients: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    phone: '',
    email: ''
  });

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/clients');

      // Fetch projects for each client
      const clientsWithProjects = await Promise.all(
        response.data.map(async (client: any) => {
          try {
            const projectsRes = await api.get(`/projects/client/${client.id}`);
            return {
              ...client,
              projects: projectsRes.data.map((p: any) => ({ id: p.id, name: p.name, status: p.status })),
              projectsCount: projectsRes.data.length
            };
          } catch (err) {
            console.error(`Error fetching projects for client ${client.id}:`, err);
            return {
              ...client,
              projects: [],
              projectsCount: 0
            };
          }
        })
      );

      setClients(clientsWithProjects);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clients');
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClientClick = (clientId: number) => {
    navigate(`/client/${clientId}/projects`);
  };

  const handleAddClient = async () => {
    if (!newClient.name || !newClient.phone) {
      alert('Please fill in name and phone number');
      return;
    }

    if (newClient.phone.length !== 10) {
      alert('Please enter a valid 10-digit phone number');
      return;
    }

    try {
      const response = await api.post('/users/clients', {
        name: newClient.name,
        phone: newClient.phone,
        email: newClient.email || undefined
      });

      // Add the new client to the list
      setClients([...clients, { ...response.data, projectsCount: 0 }]);
      setShowAddModal(false);
      setNewClient({ name: '', phone: '', email: '' });
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to add client');
      console.error('Error adding client:', err);
    }
  };

  return (
    <div className="clients-container">
      <header className="clients-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/dashboard')}>
            ← Back
          </button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
          <img src="/logo.png" alt="Arc Track" className="logo-icon-small" />
          <h1>Building Clients</h1>
        </div>
        <button className="btn-add-client" onClick={() => setShowAddModal(true)}>+ Add New Client</button>
      </header>

      <div className="clients-content">
        {loading && <div style={{ textAlign: 'center', padding: '40px', fontSize: '18px', color: '#666' }}>Loading clients...</div>}

        {error && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#f44336', fontSize: '16px', marginBottom: '20px' }}>{error}</p>
            <button
              onClick={fetchClients}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && clients.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p style={{ fontSize: '18px', marginBottom: '10px' }}>No clients yet</p>
            <p style={{ fontSize: '14px' }}>Click "+ Add New Client" to get started</p>
          </div>
        )}

        {!loading && !error && clients.length > 0 && (
          <div className="clients-grid">
            {clients.map((client) => (
              <div
                key={client.id}
                className="client-card"
                onClick={() => handleClientClick(client.id)}
              >
                <div className="client-avatar">
                  {client.name.charAt(0).toUpperCase()}
                </div>
                <div className="client-info">
                  <h3>{client.name}</h3>
                  <p className="client-phone">📱 {client.phone}</p>
                  {client.email && <p className="client-email">✉️ {client.email}</p>}
                  <div className="client-projects">
                    <span className="projects-badge">{client.projectsCount || 0} Projects</span>
                  </div>
                  {client.projects && client.projects.length > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                      <p style={{ fontSize: '12px', fontWeight: '600', color: '#666', marginBottom: '5px' }}>Projects:</p>
                      {client.projects.map((project) => (
                        <div key={project.id} style={{ fontSize: '13px', color: '#333', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                          <span>📋</span>
                          <span>{project.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add New Client</h2>
              <button className="btn-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Client Name *</label>
                <input
                  type="text"
                  placeholder="Enter client name"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  placeholder="Enter 10-digit phone number"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  maxLength={10}
                />
              </div>
              <div className="form-group">
                <label>Email (Optional)</label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
              <button className="btn-submit" onClick={handleAddClient}>Add Client</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;
